/**
 * What each visit outcome means, and what it does to the next-visit
 * cadence.
 *
 * The cooldown numbers MUST stay in lockstep with the backend constants
 * in ``hy_sales/services/field_priority.py:OUTCOME_COOLDOWN_DAYS`` —
 * that table is the source of truth for the actual suppression window.
 *
 * Extracted out of `VisitNoteDialog` so the dropdown, the impact card,
 * and the User Guide's outcome key all read from one definition. The
 * guide showing a stale cooldown would be worse than showing none.
 */

import type { VisitOutcome } from '../api/field';

export interface OutcomeGuide {
  label: string;
  /** One short sentence on when to pick this — read in the dropdown
   *  option subtitle, the impact card body, and the guide's key. */
  when: string;
  /** Days this account is suppressed from Today's Plan auto-picks
   *  after a visit with this outcome.  My Territories is unaffected. */
  cooldownDays: number;
}

export const OUTCOME_GUIDE: Record<VisitOutcome, OutcomeGuide> = {
  ordered: {
    label: 'Ordered',
    when: 'Customer placed an order — full case, sample case, or a confirmed reorder.',
    cooldownDays: 30,
  },
  follow_up_needed: {
    label: 'Follow-up needed',
    when: 'Conversation is open — new buyer onboarding, ownership change, or a pending decision.',
    cooldownDays: 7,
  },
  no_response: {
    label: 'No response',
    when: "Didn't connect — buyer was out, on vacation, in a meeting, or unreachable.",
    cooldownDays: 3,
  },
  declined: {
    label: 'Declined',
    when: 'Customer firmly passed on this offering — give them space before circling back.',
    cooldownDays: 60,
  },
  info_only: {
    label: 'Info only',
    when: 'Dropped samples, pricing, or program info — no buying commitment made.',
    cooldownDays: 14,
  },
};

/** Ordered for display: most positive outcome first. */
export const OUTCOME_ORDER: VisitOutcome[] = [
  'ordered',
  'follow_up_needed',
  'info_only',
  'no_response',
  'declined',
];
